import { Obj, VoidCallback } from '@deqo/foundation';
import { useCallback } from 'react';
import { telemetry } from '../../@vendor/Posthog';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { BoardLiteData, EntityWithLabel } from '../../api/ApiTypes';
import { waitFor } from '../../CodesThatsGottaGoSomewhereElse';
import { Host } from '../../Host';
import { ClipboardService, ClipDataBuilder } from '../../platform/Clipboard';
import { useAccountGuardCallback } from '../account/AccountHooks';
import { AccountModel } from '../account/AccountModel';
import { AppEvents } from '../AppEvents';
import { AppLocation } from '../AppLocation';
import { AppNavigator } from '../AppNavigator';
import { useResolve } from '../Container';
import { noticePopupWith } from '../popups/OptionsPopup';
import { PopupManager } from '../popups/PopupManager';
import { newBoardBuilderPopup } from '../workspace/NewBoardBuilderPopup';
import { newBoardClonePopup } from '../workspace/NewBoardClonePopup';
import { newBoardDeletePopup } from '../workspace/NewBoardDeletePopup';
import { newBoardMovePopup } from '../workspace/NewBoardMovePopup';
import { newBoardRenamePopup } from '../workspace/NewBoardRenamePopup';
import { teamSelectPopup } from '../workspace/TeamSelectPopup';

export interface CreateBoardParams {
    source: readonly ['tag' | 'template' | 'board', string];
    label?: string;
    type?: number;
    workspaceId?: string;
    folderId?: string;
    autoOpen?: boolean;
    forcePromptForLocation?: boolean;
}

const noWorkspacesPopup = () =>
    noticePopupWith(
        'Oops!',
        'You are not a member of any teams, so you cannot create boards. ' +
        'Speak to an organization admin to be added to a team.'
    );

const usePromptForLocation = () => {
    const popups = useResolve(PopupManager);
    const account = useResolve(AccountModel);
    return useCallback(async (activeWorkspaceId?: string) => {

        // If user has one workspace, and it's the one passed in, just return the root for that workspace
        if (account.workspaces.length === 1 && account.workspaces[0].id === activeWorkspaceId) {
            return { workspaceId: activeWorkspaceId, folderId: null };
        }

        // Otherwise, prompt the user for a workspace and folder
        const { actioned, data } = await popups.invoke(teamSelectPopup({
            label: 'Where do you want to create this board?',
            primary: 'Create',
        }));

        if (!actioned || !data) return null;
        return Obj.pick(data, 'workspaceId', 'folderId');

    }, []);
};

const useGetDefaultLocationIfNotSpecified = () => {
    const location = useResolve(AppLocation);
    return useCallback(({ workspaceId, folderId }: { workspaceId?: string, folderId?: string }) => {
        workspaceId = workspaceId ?? location.get('workspace');
        folderId = folderId ?? location.get('boards');
        return {
            workspaceId: workspaceId === 'null' ? null : workspaceId,
            folderId: folderId?.startsWith('FO') ? folderId : null,
        };
    }, []);
};

export const useOnCreateBoard = (onInvoke?: VoidCallback) => {
    const api = useResolve(ApiClient);
    const evts = useResolve(AppEvents);
    const nav = useResolve(AppNavigator);
    const popups = useResolve(PopupManager);
    const account = useResolve(AccountModel);

    const getDefaultLocationIfNotSpecified = useGetDefaultLocationIfNotSpecified();
    const promptForLocation = usePromptForLocation();

    const onCreateBoard = useCallback(async (params: CreateBoardParams) => {
        const { source, label, type, forcePromptForLocation, autoOpen } = { type: 1, ...params };
        const [sourceType, sourceParam] = source;
        const { workspaceId, folderId } = getDefaultLocationIfNotSpecified(params);

        if (!source) return false;

        if (account.workspaces.filter(x => ['team', 'user'].includes(x.type)).length === 0) {
            await popups.invoke(noWorkspacesPopup());
            return false;
        }

        if (sourceType === 'tag') {
            const { data } = await popups.invoke(newBoardBuilderPopup(sourceParam));
            if (!data) return false;
            await waitFor(150);
            return onCreateBoard({
                ...params,
                source: ['template', data.id],
            });
        }

        if (!workspaceId || forcePromptForLocation) {
            const data = await promptForLocation(workspaceId);
            if (!data) return false;
            return onCreateBoard({
                ...params,
                ...data,
                forcePromptForLocation: false
            });
        }

        const result = await api.exec(v2.boards.create({
            label,
            source: sourceParam,
            type,
            workspaceId,
            folderId,
        }));

        evts.push('board:create', { board: { ...result, pinned: false } });

        telemetry.emit('template.use', {
            id: sourceParam,
            name: label,
            method: 'create',
        });

        if (autoOpen) {
            const query = sourceType === 'template' ? '?action=template' : '';
            nav.go(`~/${result.id}${query}`);
        }

        onInvoke?.();

        return result.id;

    }, [onInvoke]);

    return useAccountGuardCallback(onCreateBoard, [onCreateBoard]);

};

export const useOnCopyBoardLink = (onInvoke?: VoidCallback) => {

    const clipboard = useResolve(ClipboardService);

    return useCallback((boardId: string) => {
        if (!boardId) return;

        const clipData = new ClipDataBuilder();
        clipData.setText(Host.absUrl(boardId));
        clipboard.write(clipData.build());
        onInvoke?.();

    }, [onInvoke]);
};

export type OnRenameBoardParams = {
    board: EntityWithLabel,
    value?: string,
};

export const useOnRenameBoard = (onInvoke?: VoidCallback) => {

    const popups = useResolve(PopupManager);

    const onRenameBoard = useCallback(async (board: EntityWithLabel) => {
        if (!board.id) return false;
        onInvoke?.();
        const { actioned } = await popups.invoke(newBoardRenamePopup(board));
        return actioned;
    }, [onInvoke]);

    return useAccountGuardCallback(onRenameBoard, [onRenameBoard]);
};

export const useOnMoveBoard = (onInvoke?: VoidCallback) => {

    const popups = useResolve(PopupManager);

    const onMoveBoard = useCallback(async (board: BoardLiteData) => {
        if (!board.id) return false;
        onInvoke?.();
        const { actioned } = await popups.invoke(newBoardMovePopup(board));
        return actioned;
    }, [onInvoke]);

    return useAccountGuardCallback(onMoveBoard, [onMoveBoard]);
};

export const useOnCloneBoard = (onInvoke?: VoidCallback) => {

    const popups = useResolve(PopupManager);

    const onCloneBoard = useCallback(async (board: BoardLiteData) => {
        if (!board.id) return false;
        onInvoke?.();
        const { actioned } = await popups.invoke(newBoardClonePopup(board));
        return actioned;
    }, [onInvoke]);

    return useAccountGuardCallback(onCloneBoard, [onCloneBoard]);
};

export const useOnDeleteBoard = (onInvoke?: VoidCallback) => {

    const popups = useResolve(PopupManager);

    const onDeleteBoard = useCallback(async (board: BoardLiteData) => {
        if (!board.id) return false;
        onInvoke?.();
        const { actioned } = await popups.invoke(newBoardDeletePopup(board));
        return actioned;
    }, [onInvoke]);

    return useAccountGuardCallback(onDeleteBoard, [onDeleteBoard]);
};
